// Modern Seller Setup Page
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { COLORS, LAYOUT, TYPOGRAPHY } from '../../constants';

const SellerSetup = () => {
  const [user] = useAuthState(auth);
  const [sellerStatus, setSellerStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSellerStatus = async () => {
      if (!user) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const status = userData.sellerStatus || 'not_started';
          setSellerStatus(status);
          
          // If user is already active, redirect to dashboard
          if (status === 'active') {
            navigate('/seller-dashboard', { replace: true });
          }
        }
      } catch (error) {
      }
    };

    checkSellerStatus();
  }, [user, navigate]);

  // Don't render if user is active (will be redirected)
  if (sellerStatus === 'active') {
    return null;
  }

  return (
    <div style={modernStyles.container}>
      {/* Hero Section */}
      <div style={modernStyles.heroSection}>
        <div style={modernStyles.heroContent}>
          <h1 style={modernStyles.heroTitle}>
            Start Your Selling Journey
          </h1>
          
          <p style={modernStyles.heroSubtitle}>
            Join the ISU student marketplace community. 
            Set up your seller account in just 5 minutes and start earning today!
          </p>
        </div>
      </div>

      {/* Benefits Section */}
      <div style={modernStyles.benefitsSection}>
        <h2 style={modernStyles.sectionTitle}>Why Sell on Redbird Markets?</h2>
        
        <div style={modernStyles.benefitsGrid}>
          <div style={modernStyles.benefitCard}>
            <div style={{
              ...modernStyles.benefitIconContainer,
              background: `linear-gradient(135deg, ${COLORS.primary}, #a91b2e)`,
            }}>
              <svg style={modernStyles.benefitIcon} fill="#FFFFFF" viewBox="0 0 24 24">
                <path d="M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z"/>
              </svg>
            </div>
            <h3 style={modernStyles.benefitTitle}>Earn Extra Cash</h3>
            <p style={modernStyles.benefitDescription}>
              Turn your unused textbooks, electronics, and dorm items into instant cash. 
              Average sellers make $200+ per month.
            </p>
            <div style={modernStyles.benefitFeatures}>
              <div style={modernStyles.featureItem}>✓ Instant payouts</div>
              <div style={modernStyles.featureItem}>✓ No listing fees</div>
              <div style={modernStyles.featureItem}>✓ Competitive pricing</div>
            </div>
          </div>

          <div style={modernStyles.benefitCard}>
            <div style={{
              ...modernStyles.benefitIconContainer,
              background: `linear-gradient(135deg, ${COLORS.primary}, #a91b2e)`,
            }}>
              <svg style={modernStyles.benefitIcon} fill="#FFFFFF" viewBox="0 0 24 24">
                <path d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z"/>
              </svg>
            </div>
            <h3 style={modernStyles.benefitTitle}>Student-First Platform</h3>
            <p style={modernStyles.benefitDescription}>
              Built by ISU students for ISU students. Connect with your campus community 
              and find exactly what you need.
            </p>
            <div style={modernStyles.benefitFeatures}>
              <div style={modernStyles.featureItem}>✓ Campus pickup options</div>
              <div style={modernStyles.featureItem}>✓ Student verification</div>
              <div style={modernStyles.featureItem}>✓ Semester schedules</div>
            </div>
          </div>

          <div style={modernStyles.benefitCard}>
            <div style={{
              ...modernStyles.benefitIconContainer,
              background: `linear-gradient(135deg, ${COLORS.primary}, #a91b2e)`,
            }}>
              <svg style={modernStyles.benefitIcon} fill="#FFFFFF" viewBox="0 0 24 24">
                <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.5 16,12.4 16,13V16C16,17.4 15.4,18 14.8,18H9.2C8.6,18 8,17.4 8,16V13C8,12.4 8.6,11.5 9.2,11.5V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,10V11.5H13.5V10C13.5,8.7 12.8,8.2 12,8.2Z"/>
              </svg>
            </div>
            <h3 style={modernStyles.benefitTitle}>Safe & Secure</h3>
            <p style={modernStyles.benefitDescription}>
              Your transactions are protected by industry-leading security. 
              Stripe handles all payments with bank-level encryption.
            </p>
            <div style={modernStyles.benefitFeatures}>
              <div style={modernStyles.featureItem}>✓ Secure payments</div>
              <div style={modernStyles.featureItem}>✓ Buyer protection</div>
              <div style={modernStyles.featureItem}>✓ Identity verification</div>
            </div>
          </div>
        </div>
      </div>

      {/* Setup CTA Section */}
      <div style={modernStyles.ctaSection}>
        <div style={modernStyles.ctaCard}>
          <div style={modernStyles.ctaHeader}>
            <div style={modernStyles.ctaIconContainer}>
              <svg style={modernStyles.ctaIcon} fill="#FFFFFF" viewBox="0 0 24 24">
                <path d="M2.81,14.12L5.64,11.29L8.17,10.79C11.39,6.41 17.55,4.22 19.78,4.22C19.78,6.45 17.59,12.61 13.21,15.83L12.71,18.36L9.88,21.19L9.17,17.66C7.76,17.66 7.76,17.66 7.05,16.95C6.34,16.24 6.34,16.24 6.34,14.83L2.81,14.12Z"/>
              </svg>
            </div>
            <div style={modernStyles.ctaContent}>
              <h2 style={modernStyles.ctaTitle}>Complete Your Seller Setup</h2>
              <p style={modernStyles.ctaDescription}>
                Get your seller account ready in just 5 minutes. Connect your bank account, 
                verify your identity, and start earning immediately!
              </p>
            </div>
          </div>
          
          <div style={modernStyles.ctaSteps}>
            <div style={modernStyles.stepItem}>
              <div style={modernStyles.stepNumber}>1</div>
              <span style={modernStyles.stepText}>Verify Identity</span>
            </div>
            <div style={modernStyles.stepArrow}>→</div>
            <div style={modernStyles.stepItem}>
              <div style={modernStyles.stepNumber}>2</div>
              <span style={modernStyles.stepText}>Connect Bank</span>
            </div>
            <div style={modernStyles.stepArrow}>→</div>
            <div style={modernStyles.stepItem}>
              <div style={modernStyles.stepNumber}>3</div>
              <span style={modernStyles.stepText}>Start Selling</span>
            </div>
          </div>
          
          <div style={modernStyles.ctaButtons}>
            <Link 
              to="/seller-onboarding" 
              className="setup-primary-button" 
              style={modernStyles.primaryButton}
              onClick={() => {
                // Navigation handled by Link component
              }}
            >
              <svg style={modernStyles.buttonIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
              </svg>
              <span>Complete Setup Now</span>
              <span className="button-arrow" style={modernStyles.buttonArrow}>→</span>
            </Link>
            
            <Link to="/market" className="setup-secondary-button" style={modernStyles.secondaryButton}>
              <svg style={modernStyles.buttonIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z"/>
              </svg>
              <span>Browse Market First</span>
            </Link>
          </div>
          
          <div style={modernStyles.ctaNote}>
            <svg style={modernStyles.noteIcon} fill="#15803d" viewBox="0 0 24 24">
              <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
            </svg>
            <span style={modernStyles.noteText}>
              Setup is completely free and takes less than 5 minutes
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const modernStyles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  
  heroSection: {
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, #a91b2e 100%)`,
    position: 'relative',
    overflow: 'hidden',
    padding: `${LAYOUT.spacing.xxl} ${LAYOUT.spacing.xl}`,
    textAlign: 'center',
  },
  
  heroContent: {
    maxWidth: '800px',
    margin: '0 auto',
    position: 'relative',
    zIndex: 2,
  },
  
  heroTitle: {
    ...TYPOGRAPHY.heading1,
    color: 'white',
    fontSize: '4rem',
    fontWeight: '900',
    marginBottom: LAYOUT.spacing.lg,
    textShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    lineHeight: '1.1',
  },
  
  heroSubtitle: {
    fontSize: '1.3rem',
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: '1.6',
    marginBottom: LAYOUT.spacing.xxl,
    maxWidth: '600px',
    margin: `0 auto ${LAYOUT.spacing.xxl}`,
  },
  
  benefitsSection: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: `${LAYOUT.spacing.xxl} ${LAYOUT.spacing.xl}`,
  },
  
  sectionTitle: {
    ...TYPOGRAPHY.heading2,
    fontSize: '3rem',
    fontWeight: '800',
    color: COLORS.text.primary,
    textAlign: 'center',
    marginBottom: LAYOUT.spacing.xxl,
  },
  
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: LAYOUT.spacing.xl,
  },
  
  benefitCard: {
    backgroundColor: 'white',
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xxl,
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    textAlign: 'center',
    ':hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
  },
  
  benefitIconContainer: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: `0 auto ${LAYOUT.spacing.lg}`,
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
  },
  
  benefitIcon: {
    width: '32px',
    height: '32px',
  },
  
  benefitTitle: {
    fontSize: '1.8rem',
    fontWeight: '700',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.md,
  },
  
  benefitDescription: {
    fontSize: '1.1rem',
    color: COLORS.text.muted,
    lineHeight: '1.6',
    marginBottom: LAYOUT.spacing.lg,
  },
  
  benefitFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.sm,
  },
  
  featureItem: {
    fontSize: '0.95rem',
    color: COLORS.text.secondary,
    fontWeight: '500',
    textAlign: 'left',
  },
  
  ctaSection: {
    backgroundColor: '#f1f5f9',
    padding: `${LAYOUT.spacing.xxl} ${LAYOUT.spacing.xl}`,
  },
  
  ctaCard: {
    maxWidth: '900px',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xxl,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
  },
  
  ctaHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: LAYOUT.spacing.lg,
    marginBottom: LAYOUT.spacing.xl,
    textAlign: 'left',
  },
  
  ctaIconContainer: {
    width: '80px',
    height: '80px',
    backgroundColor: COLORS.primary,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 8px 16px rgba(206, 17, 38, 0.3)',
  },
  
  ctaIcon: {
    width: '32px',
    height: '32px',
  },
  
  ctaContent: {
    flex: 1,
  },
  
  ctaTitle: {
    fontSize: '2.5rem',
    fontWeight: '800',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.md,
    lineHeight: '1.2',
  },
  
  ctaDescription: {
    fontSize: '1.2rem',
    color: COLORS.text.muted,
    lineHeight: '1.6',
  },
  
  ctaSteps: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.lg,
    marginBottom: LAYOUT.spacing.xxl,
    flexWrap: 'wrap',
  },
  
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
  },
  
  stepNumber: {
    width: '40px',
    height: '40px',
    backgroundColor: COLORS.primary,
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '1.1rem',
  },
  
  stepText: {
    fontSize: '1rem',
    fontWeight: '600',
    color: COLORS.text.secondary,
  },
  
  stepArrow: {
    fontSize: '1.5rem',
    color: COLORS.text.muted,
    fontWeight: '600',
  },
  
  ctaButtons: {
    display: 'flex',
    gap: LAYOUT.spacing.md,
    justifyContent: 'center',
    marginBottom: LAYOUT.spacing.lg,
    flexWrap: 'wrap',
  },
  
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    backgroundColor: COLORS.primary,
    color: 'white',
    padding: `${LAYOUT.spacing.lg} ${LAYOUT.spacing.xxl}`,
    borderRadius: LAYOUT.borderRadius.lg,
    textDecoration: 'none',
    fontSize: '1.2rem',
    fontWeight: '700',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 14px 0 rgba(206, 17, 38, 0.4)',
    border: 'none',
    cursor: 'pointer',
  },
  
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    backgroundColor: 'transparent',
    color: COLORS.primary,
    border: `2px solid ${COLORS.primary}`,
    padding: `${LAYOUT.spacing.lg} ${LAYOUT.spacing.xxl}`,
    borderRadius: LAYOUT.borderRadius.lg,
    textDecoration: 'none',
    fontSize: '1.2rem',
    fontWeight: '700',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
  },
  
  buttonIcon: {
    width: '20px',
    height: '20px',
  },
  
  buttonArrow: {
    fontSize: '1.2rem',
    transition: 'transform 0.3s ease',
  },
  
  ctaNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.sm,
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.lg}`,
    borderRadius: LAYOUT.borderRadius.lg,
    border: '1px solid rgba(34, 197, 94, 0.2)',
  },
  
  noteIcon: {
    width: '18px',
    height: '18px',
  },
  
  noteText: {
    fontSize: '0.95rem',
    color: '#15803d',
    fontWeight: '600',
  },
};

// Add floating and button animations
const sellerSetupAnimations = `
  @keyframes float {
    0%, 100% { 
      transform: translateY(0px); 
    }
    50% { 
      transform: translateY(-20px); 
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes pulse {
    0%, 100% { 
      transform: scale(1); 
    }
    50% { 
      transform: scale(1.05); 
    }
  }
  
  .seller-setup-card {
    animation: fadeInUp 0.6s ease-out;
  }
  
  .seller-setup-card:nth-child(1) { animation-delay: 0.1s; }
  .seller-setup-card:nth-child(2) { animation-delay: 0.2s; }
  .seller-setup-card:nth-child(3) { animation-delay: 0.3s; }
  
  .setup-primary-button {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  
  .setup-primary-button:hover {
    background-color: #a91b2e !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 15px 0 rgba(206, 17, 38, 0.4) !important;
  }
  
  .setup-primary-button:active {
    transform: translateY(0px) !important;
    transition: all 0.1s ease !important;
  }
  
  .setup-secondary-button {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  
  .setup-secondary-button:hover {
    background-color: #CE1126 !important;
    color: white !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px 0 rgba(206, 17, 38, 0.3) !important;
  }
  
  .setup-secondary-button:active {
    transform: translateY(0px) !important;
    transition: all 0.1s ease !important;
  }
  
  .setup-primary-button:hover .button-arrow {
    transform: translateX(4px) !important;
    transition: transform 0.3s ease !important;
  }
  
  .setup-secondary-button:hover svg {
    transform: scale(1.05) !important;
    transition: transform 0.3s ease !important;
  }
`;

// Inject seller setup styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('seller-setup-animations');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'seller-setup-animations';
    style.textContent = sellerSetupAnimations;
    document.head.appendChild(style);
  }
}

export default SellerSetup;